import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import SwitchSelector from 'react-switch-selector';
import { showLoader, hideLoader } from '../../store/common/slice';
import { useAccessToken } from '../../common/hooks/useAccessToken';
import Button from '../../common/Button';
import { isAdminSelector } from '../../store/auth/selectors';
import { StyledReport } from '../reports/report.styled';
import Search from '../../../components/images/search.svg';
import { ActiveMonitoringWrapper, ActiveMonitoringTabs } from './Monitoring.styled';
import AptMonitoringList from './AptMonitoringList';
import { sortApartmentNumbers } from '../../common/shared/statelessMethods';
import {parseApartmentPhone, parseApartmentTenantName} from "../../store/common/tenantInfo";
import {NotificationManager} from "react-notifications";

const tabs = {
    active: 'active',
    stopped: 'stopped',
}

const Monitoring = ({ match }) => {
    const { t } = useTranslation();
    const client = useAccessToken();
    const dispatch = useDispatch();
    const isAdmin = useSelector(isAdminSelector);
    const ltr = useSelector(state => state.common.ltrEnabled);
    const { buildingID } = match.params;

    const [building, setBuilding] = useState(null);
    const [filter, setFilter] = useState('');
    const [currentTab, setCurrentTab] = useState(tabs.active);

    const [aptsInMonitoring, setAptsInMonitoring] = useState([]);
    const [aptsMonitoringStopped, setAptsMonitoringStopped] = useState([]);
    const [totalTenants, setTotalTenants] = useState();
    const [isBuildingWithMonitoringLoading, setIsBuildingWithMonitoringLoading] = useState(false);
    const [isBuildingWithMonitoring, setIsBuildingWithMonitoring] = useState(null);


    const components = {
        [tabs.active]: {
            component: AptMonitoringList,
            props: {
                records: aptsInMonitoring,
                filter,
                inMonitoring: true,
                buildingID: buildingID
            }
        },
        [tabs.stopped]: {
            component: AptMonitoringList,
            props: {
                records: aptsMonitoringStopped,
                filter,
                inMonitoring: false,
                buildingID: buildingID
            }
        }
    };
    const tabComponent = components[currentTab];

    useEffect(() => {
        if (!client) return;
        (async function () {
            dispatch(showLoader());

            const result = await client.managers.buildings.getManagerBuilding(
                buildingID, {skipStats: true, includeInternalID: true}
            );

            if (!result.building?.buildingInternal) {
                throw new Error('Building is not loaded');
            }
            setBuilding(result.building);

            const {inMonitoring, stopped} = await client.monitoring.getBuildingState(buildingID);
            const process = apts => apts.sort((a, b) => sortApartmentNumbers(a.ApartmentNum, b.ApartmentNum)).map(apt => {
                apt.tenantPhone = parseApartmentPhone(apt.tenantDetails);
                apt.tenantName = parseApartmentTenantName(apt.tenantDetails);
                return apt;
            });

            setAptsInMonitoring(process(inMonitoring));
            setAptsMonitoringStopped(process(stopped));
            setTotalTenants(inMonitoring.length + stopped.length);

            setIsBuildingWithMonitoringLoading(true);
            const apartmentsMonitoringFeature = await client.operations.getFeature('buildingID', result.building.buildingInternal, 'apartmentsMonitoring');
            // todo: add a partner level visibility here
            setIsBuildingWithMonitoring(apartmentsMonitoringFeature?.value === 'true');
            setIsBuildingWithMonitoringLoading(false);

            dispatch(hideLoader());
        })();
    }, [client]);

    const onClickMonitoringSwitch = async () => {
        try {
            setIsBuildingWithMonitoringLoading(true);

            if (!building?.buildingInternal) {
                throw new Error('Building is not loaded');
            }

            const newValue = isBuildingWithMonitoring ? 'false' : 'true';
            const result = await client.operations.setFeature('buildingID', building.buildingInternal, 'apartmentsMonitoring', newValue);
            setIsBuildingWithMonitoring(result?.value === 'true');
            setIsBuildingWithMonitoringLoading(false);
            if (isBuildingWithMonitoring) {
                NotificationManager.success(t('monitoring.activeMonitoring.successfullyAllStopped'));
            } else {
                NotificationManager.success(t('monitoring.activeMonitoring.successfullyAllStarted'));
            }

            setTimeout(() => window.location.reload(), 2000);
        } catch (e) {
            setIsBuildingWithMonitoringLoading(false);
            NotificationManager.error(t('monitoring.activeMonitoring.somethingWentWrong'));
        }
    }

    if (!isAdmin) return <div>{t('searchPayments.noAdminRights')}</div>;

    const switchMonitoringTitle = isBuildingWithMonitoring ? t('monitoring.activeMonitoring.btns.stopAll') : t('monitoring.activeMonitoring.btns.startAll');
    return (
        <ActiveMonitoringWrapper>
            <h1 className='page-title text-only'>
                <span>{t('monitoring.activeMonitoring.pageTitle')}</span>
            </h1>
            <div className='top-panel'>
                <div className='top-panel-title'>
                    {t('monitoring.activeMonitoring.totalTenants')} <span className='total-num'>{totalTenants}</span>
                </div>
                {isBuildingWithMonitoring !== null && isAdmin &&
                    <div>
                        <Button disabled={isBuildingWithMonitoringLoading} onClick={onClickMonitoringSwitch}>{switchMonitoringTitle}</Button>
                    </div>
                }
                <div className='top-panel-inside'>
                    <StyledReport className='report' ltr={ltr}>
                        <div className='report-wrap'>
                            <div className='report-top'>
                                <div className='report-top-start'>
                                    <div className="search">
                                        <input
                                            type="text"
                                            name="search"
                                            onChange={e => setFilter(e.target.value.toLowerCase())}
                                        />
                                        <img src={Search} alt={t('search.search')} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </StyledReport>
                    <ActiveMonitoringTabs className={currentTab === tabs.active ? 'active-selected' : ''}>
                        <SwitchSelector
                            initialSelectedIndex={2}
                            options={[
                                {
                                    label: t('monitoring.activeMonitoring.tabs.inMonitoring'),
                                    value: tabs.active,
                                    selectedBackgroundColor: 'white',
                                },
                                {
                                    label: t('monitoring.activeMonitoring.tabs.withoutMonitoring'),
                                    value: tabs.stopped,
                                    selectedBackgroundColor: 'white',
                                }
                            ]}
                            selectedFontColor='#6E74FF'
                            fontColor='#ffffff'
                            backgroundColor='linear-gradient(90deg, #6C90FF -0.23%, #8567FF 92.15%)'
                            onChange={e => setCurrentTab(e)}
                        />
                    </ActiveMonitoringTabs>
                </div>
            </div>
            <tabComponent.component
                {...tabComponent.props}
            />
        </ActiveMonitoringWrapper>
    );
};

export default Monitoring;
